class GiftingBookingForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector("form");
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.messageContainer = this.form.querySelector(".form-status-message");
    this.scriptUrl = this.dataset.scriptUrl || "";

    this.validationRules = {
      email: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address",
      },
      phone: {
        minLength: 10,
        message: "Please enter at least 10 digits",
      },
    };

    this.init();
  }

  init() {
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
    this.setupRealTimeValidation();
  }

  setupRealTimeValidation() {
    const inputs = this.form.querySelectorAll("input, textarea, select");

    inputs.forEach((input) => {
      /* ===============================
         DIGITS ONLY – PHONE
      =============================== */
      if (input.type === "tel") {
        input.addEventListener("input", (e) => {
          const cleaned = e.target.value.replace(/[^0-9]/g, "");
          if (e.target.value !== cleaned) {
            e.target.value = cleaned;
          }
        });
      }

      /* ===============================
         VALIDATE ON BLUR
      =============================== */
      input.addEventListener("blur", () => {
        if (input.value.trim() !== "" || input.hasAttribute("required")) {
          this.validateField(input);
        }
      });

      /* ===============================
         CLEAR ERROR ON FOCUS
      =============================== */
      input.addEventListener("focus", () => {
        this.clearFieldError(input);
      });

      /* ===============================
         DEBOUNCED LIVE VALIDATION
      =============================== */
      if (input.type === "email" || input.type === "tel") {
        input.addEventListener(
          "input",
          this.debounce(() => {
            if (input.value.trim() !== "") {
              this.validateField(input);
            }
          }, 500)
        );
      }
    });
  }

  validateField(field) {
    const fieldType = field.type;
    const fieldValue = field.value.trim();

    this.clearFieldError(field);

    /* ===============================
       REQUIRED
    =============================== */
    if (field.hasAttribute("required") && fieldValue === "") {
      this.showFieldError(field, "This field is required");
      return false;
    }

    if (fieldValue === "") return true;

    /* ===============================
       EMAIL
    =============================== */
    if (fieldType === "email") {
      if (!this.validationRules.email.pattern.test(fieldValue)) {
        this.showFieldError(field, this.validationRules.email.message);
        return false;
      }
    }

    /* ===============================
       PHONE – FIXED VALIDATION
    =============================== */
    if (fieldType === "tel") {
      if (!/^\d+$/.test(fieldValue)) {
        this.showFieldError(field, "Only numbers are allowed");
        return false;
      }

      if (fieldValue.length < 10) {
        this.showFieldError(field, "Please enter at least 10 digits");
        return false;
      }
    }

    /* ===============================
       TEXT MIN LENGTH
    =============================== */
    if (fieldType === "text" && field.hasAttribute("minlength")) {
      const minLength = parseInt(field.getAttribute("minlength"), 10);
      if (fieldValue.length < minLength) {
        this.showFieldError(
          field,
          `This field must be at least ${minLength} characters`
        );
        return false;
      }
    }

    /* ===============================
       CUSTOM PATTERN
    =============================== */
    if (field.hasAttribute("pattern")) {
      const pattern = new RegExp(field.getAttribute("pattern"));
      if (!pattern.test(fieldValue)) {
        this.showFieldError(
          field,
          field.getAttribute("title") || "Please match the requested format"
        );
        return false;
      }
    }

    return true;
  }

  validateForm() {
    const inputs = this.form.querySelectorAll("input, textarea, select");
    let isValid = true;
    let firstInvalidField = null;

    inputs.forEach((input) => {
      if (!this.validateField(input)) {
        isValid = false;
        if (!firstInvalidField) {
          firstInvalidField = input;
        }
      }
    });

    if (firstInvalidField) {
      firstInvalidField.focus();
    }

    return isValid;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.validateForm()) {
      this.showMessage("Please fix the errors above", "error");
      return;
    }

    this.setLoading(true);

    const formData = new FormData(this.form);
    formData.append("timestamp", new Date().toISOString());

    try {
      await fetch(this.scriptUrl, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      this.showMessage("Your request has been sent successfully!", "success");
      this.form.reset();

      if (window.dataLayer) {
        window.dataLayer.push({
          event: "form_submission",
          form_name: "gifting_booking",
        });
      }

      setTimeout(() => {
        this.messageContainer.style.display = "none";
      }, 5000);
    } catch (error) {
      console.error("Submission error:", error);
      this.showMessage("Something went wrong. Please try again.", "error");
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    const buttonText = this.submitButton.querySelector(".button-text");
    const buttonLoader = this.submitButton.querySelector(".button-loader");
    const buttonIcon = this.submitButton.querySelector(".button-icon");

    this.submitButton.disabled = isLoading;

    if (isLoading) {
      this.submitButton.classList.add("button--loading");
      if (buttonText) buttonText.style.opacity = "0";
      if (buttonIcon) buttonIcon.style.opacity = "0";
      if (buttonLoader) buttonLoader.style.display = "flex";
    } else {
      this.submitButton.classList.remove("button--loading");
      if (buttonText) buttonText.style.opacity = "1";
      if (buttonIcon) buttonIcon.style.opacity = "1";
      if (buttonLoader) buttonLoader.style.display = "none";
    }
  }

  showMessage(message, type) {
    this.messageContainer.textContent = message;
    this.messageContainer.className = `form-status-message form-status-message--${type}`;
    this.messageContainer.style.display = "block";
    this.messageContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  clearFieldError(field) {
    const fieldContainer = field.closest(".form-field");

    field.classList.remove("form-input--error");
    field.removeAttribute("aria-invalid");

    const errorElement =
      fieldContainer?.querySelector(".form-error") ||
      (field.nextElementSibling?.classList.contains("form-error")
        ? field.nextElementSibling
        : null);

    if (errorElement) {
      errorElement.textContent = "";
      errorElement.style.display = "none";
    }
  }

  showFieldError(field, message) {
    const fieldContainer = field.closest(".form-field");

    field.classList.add("form-input--error");
    field.setAttribute("aria-invalid", "true");

    let errorElement = fieldContainer?.querySelector(".form-error");

    if (!errorElement) {
      errorElement = document.createElement("span");
      errorElement.className = "form-error";
      errorElement.setAttribute("role", "alert");

      if (fieldContainer) {
        fieldContainer.appendChild(errorElement);
      } else {
        field.insertAdjacentElement("afterend", errorElement);
      }
    }

    errorElement.textContent = message;
    errorElement.style.display = "block";
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.define("gifting-booking-form", GiftingBookingForm);
